import axios, { AxiosInstance } from 'axios'
import { Module } from 'vuex'

import { apolloCache } from '@/apollo-provider'

export interface FactoryConfig {
  baseUrl: string
}

export interface ProfileType {
  profile: RegistrationData
}

export interface UserType {
  user: Profile
}

export interface TokenType {
  authToken: string
}

export interface ErrorsType {
  errors: object
}

import {
  Credentials,
  ModuleState,
  params,
  Profile,
  RegistrationData,
  RootState,
  types,
} from './types'
import { Token } from 'graphql'

export function factory({ baseUrl }: FactoryConfig): Module<ModuleState, RootState> {
  return {
    actions: {
      INITIALIZE_STORE: {
        root: true,
        handler({ state, dispatch }) {
          if (state.authToken !== undefined) {
            const userJson: string | null = sessionStorage.getItem('currentUser')
            if (userJson) {
              state.currentUser = JSON.parse(userJson)
            }
            dispatch({ type: 'loadUserProfile' })
          } else {
            console.log('authToken is undefined')
          }
        },
      },
      async [types.actions.Register]({ commit }, { profile }: ProfileType) {
        return axios
          .post<Profile>(
            `${baseUrl}/users/register`,
            { user: profile },
            {
              headers: {
                Accept: 'application/json',
              },
            }
          )
          .then(({ data }) => {
            commit<params.mutations.SetCurrentUser>({
              type: types.mutations.SetCurrentUser,
              user: data,
            })
          })
          .catch(reason => {
            if (reason.response && reason.response.data) {
              commit<params.mutations.SetRegistrationErrors>({
                errors: reason.response.data,
                type: types.mutations.SetRegistrationErrors,
              })
            } else {
              throw reason
            }
          })
      },
      async loadUserProfile({ commit, dispatch, getters: { http } }) {
        return (http as AxiosInstance)
          .get<Profile>(`${baseUrl}/users/me`)
          .then(({ data: user }) => {
            commit<params.mutations.SetCurrentUser>({
              type: types.mutations.SetCurrentUser,
              user,
            })
            dispatch({ type: 'USER_SIGNED_IN', global: true })
            return user
          })
          .catch(error => {
            console.log('Error getting users/me - reason: ' + error)
          })
      },
      async logout({ commit, dispatch }) {
        await apolloCache.reset()
        commit({ type: 'logout' })
        dispatch({ type: 'USER_LOGGED_OUT', global: true })
      },
      async [types.actions.Login](
        { commit, dispatch, getters: { http } },
        { credentials }: params.actions.Login
      ) {
        return (http as AxiosInstance)
          .post<{ encoded: string }>(`${baseUrl}/tokens`, credentials, {
            headers: {
              Accept: 'application/json',
            },
          })
          .then(({ data: token }) => {
            commit<params.mutations.SetAuthToken>({
              authToken: token.encoded,
              type: types.mutations.SetAuthToken,
            })

            return dispatch({ type: 'loadUserProfile' }).then(user => {
              return { user, token }
            })
          })
          .catch(reason => {
            commit<params.mutations.SignalFailedLogin>({
              type: types.mutations.SignalFailedLogin,
            })
            throw reason
          })
      },
    },
    getters: {
      authToken: state => state.authToken,
      currentUser: state => state.currentUser,
      currentUserId: (state, getters) => getters.currentUser && getters.currentUser.id,
      isSignedIn: state => state.currentUser !== undefined,
      httpHeaders(state) {
        if (state.authToken !== undefined) {
          return {
            Accept: 'application/json',
            Authorization: `Bearer ${state.authToken}`,
            'Content-Type': 'application/json;charset=UTF-8',
          }
        } else {
          return {
            Accept: 'application/json',
            'Content-Type': 'application/json;charset=UTF-8',
          }
        }
      },
      http(state, getters): AxiosInstance {
        return axios.create({
          headers: getters.httpHeaders,
        })
      },
    },
    mutations: {
      [types.mutations.SetCurrentUser](state, { user }: UserType) {
        window.sessionStorage.removeItem('currentUser')
        state.currentUser = user
        window.sessionStorage.setItem('currentUser', JSON.stringify(user))
      },
      [types.mutations.SetAuthToken](state, { authToken }: TokenType) {
        window.sessionStorage.setItem('jwt', authToken)
        state.authToken = authToken
      },
      [types.mutations.SetRegistrationErrors](state, { errors }: ErrorsType) {
        state.registrationErrors = errors
      },
      logout(state) {
        state.authToken = undefined
        state.currentUser = undefined
        window.sessionStorage.removeItem('jwt')
        window.sessionStorage.removeItem('currentUser')
      },
    },
    state: {
      authToken: window.sessionStorage.getItem('jwt') || undefined,
      currentUser: undefined,
      loginFailed: undefined,
      registrationErrors: {},
    },
  }
}
